import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { Wordmark } from './Wordmark';

// Selector de producto de la familia Veta (Horas · Desk · Change), igual al de Veta Horas
// (ascendix-hours-portal/frontend/src/components/FamilyShell.jsx). Solo muestra los
// módulos prendidos para la empresa.

const VETA_URL = ((import.meta.env.VITE_VETA_URL as string | undefined) ?? 'https://horas.ascendix.com.mx').replace(/\/$/, '');

type ProductId = 'horas' | 'desk' | 'change';

const ICON: Record<ProductId, ReactNode> = {
  horas: <path d="M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />,
  desk: <path d="M4 5h16v11H8l-4 4V5z" />,
  change: <path d="M4 7h11l-3-3M20 17H9l3 3" />
};

export function ProductSwitcher() {
  const { me } = useSession();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const inChange = location.pathname.startsWith('/cambios');
  const current: ProductId = inChange ? 'change' : 'desk';
  const items: { id: ProductId; name: string; desc: string; to?: string; href?: string }[] = [
    { id: 'horas', name: 'Veta Horas', desc: 'Captura, reportes y cierre mensual', href: VETA_URL },
    { id: 'desk', name: 'Veta Desk', desc: 'Tickets, SLA y portal del cliente', to: '/' },
    ...(me?.modules?.change ? [{ id: 'change' as const, name: 'Veta Change', desc: 'Estimación, cotización y pase a producción', to: '/cambios' }] : [])
  ];

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
        aria-label={`Veta ${current} · cambiar de producto`}
        className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-[#2A2926]">
        <Wordmark product={current} />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9C7C2" strokeWidth="2" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-40 mt-2 w-80 overflow-hidden rounded-lg border border-line bg-white p-1.5 text-ink shadow-lg">
          <p className="px-3 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Familia Veta</p>
          {items.map(it => {
            const here = it.id === current;
            const cls = `flex items-center gap-3 rounded-md px-3 py-2.5 ${here ? 'bg-accent-soft' : 'hover:bg-paper'}`;
            const body = (
              <>
                <span className={`grid h-8 w-8 flex-none place-items-center rounded-md ${here ? 'bg-accent text-white' : 'bg-paper text-ink'}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">{ICON[it.id]}</svg>
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-semibold">{it.name}</span>
                  <span className="text-xs text-muted">{it.desc}</span>
                </span>
                {here
                  ? <span className="text-xs font-semibold text-accent">Aquí</span>
                  : it.href ? <span className="text-xs text-muted" aria-hidden="true">↗</span> : null}
              </>
            );
            return it.href
              ? <a key={it.id} role="menuitem" href={it.href} className={cls} onClick={() => setOpen(false)}>{body}</a>
              : <Link key={it.id} role="menuitem" to={it.to ?? '/'} aria-current={here ? 'page' : undefined} className={cls} onClick={() => setOpen(false)}>{body}</Link>;
          })}
        </div>
      )}
    </div>
  );
}
